import React from "react";
import styled from "styled-components";
import {Link} from "react-router-dom";
import AboutMe from "../components/Sections/AboutMe";
import HowIWork from "../components/Sections/HowIWork";
import CaseStudies from "../components/Sections/CaseStudies";
import {useMediaQuery} from "react-responsive";
import {mailtoHref, SITE} from "site";

export default function EasyModePage() {
    const isTabletOrMobile = useMediaQuery({query: "(max-width: 1224px)"});
    const isShort = useMediaQuery({query: "(max-height: 820px)"});

    return (
        <Shell data-testid="bio-shell" $isTabletOrMobile={isTabletOrMobile}>
            <SkipLink href="#main-content">Skip to profile</SkipLink>
            <Frame>
            {!isTabletOrMobile && (
                <DesktopLink to="/">Desktop</DesktopLink>
            )}
            <Page
                data-testid="bio-page"
                data-layout={isTabletOrMobile ? "stack" : "fill-cases"}
                $stack={isTabletOrMobile}
                $short={isShort}
            >
                <Top $stack={isTabletOrMobile}>
                    <Main id="main-content" tabIndex={-1}>
                        <AboutMe/>
                    </Main>
                    <Aside aria-label="How I work">
                        <HowIWork/>
                    </Aside>
                </Top>
                <CaseStudies/>
                <More aria-label="Projects and social">
                        <MoreRow>
                            <MoreLabel>Projects</MoreLabel>
                            <a href={mailtoHref}>
                                Splitrock Consulting
                            </a>
                            <span aria-hidden="true">·</span>
                            <span>My Ledger</span>
                            <span aria-hidden="true">·</span>
                            <a
                                href={SITE.firstGithub}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                FIRST Team 95
                            </a>
                        </MoreRow>
                        <MoreRow>
                            <MoreLabel>Social</MoreLabel>
                            <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer">
                                LinkedIn
                            </a>
                            <span aria-hidden="true">·</span>
                            <a href={SITE.github} target="_blank" rel="noopener noreferrer">
                                GitHub
                            </a>
                            <span aria-hidden="true">·</span>
                            <a href={mailtoHref}>Email</a>
                        </MoreRow>
                    </More>
            </Page>
            </Frame>
        </Shell>
    );
}

interface IShell {
    $isTabletOrMobile?: boolean;
}

const focusRing = `
  &:focus-visible {
    outline: 2px solid #f0f0f0;
    outline-offset: 3px;
  }
`;

const SkipLink = styled.a`
  position: absolute;
  left: 16px;
  top: 16px;
  z-index: 3;
  padding: 8px 12px;
  background: #121212;
  border: 2px solid #f0f0f0;
  text-decoration: none;
  font-size: 14px;
  transform: translateY(-200%);
  &:focus {
    transform: none;
  }

  /* Win over Shell a { color: inherit } (class + element). */
  && {
    color: #f4f4f4;
  }
`;

const Shell = styled.div<IShell>`
  position: fixed;
  inset: 0;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  padding: ${(props) => (props.$isTabletOrMobile ? "12px" : "24px")};

  a {
    color: inherit;
    ${focusRing}
  }
`;

const Frame = styled.div`
  position: relative;
  flex: 1;
  width: 100%;
  max-width: none;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
  border: 2px solid #8a8a8a;
`;

const Page = styled.div<{ $stack: boolean; $short: boolean }>`
  height: 100%;
  overflow-x: hidden;
  overflow-y: ${(props) => (props.$stack ? "auto" : "hidden")};
  -webkit-overflow-scrolling: touch;
  padding: ${(props) =>
    props.$stack ? "16px" : props.$short ? "12px 24px 8px" : "20px 28px 12px"};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$stack ? "16px" : props.$short ? "8px" : "12px")};
  min-height: 0;
  min-width: 0;
`;

const Top = styled.div<{ $stack: boolean }>`
  display: grid;
  grid-template-columns: ${(props) => (props.$stack ? "minmax(0, 1fr)" : "minmax(0, 2.6fr) minmax(260px, 0.8fr)")};
  gap: ${(props) => (props.$stack ? "16px" : "16px 40px")};
  flex: 0 0 auto;
  min-height: 0;
  min-width: 0;
`;

const Main = styled.main`
  min-height: 0;
  overflow: hidden;

  &:focus {
    outline: 2px solid #f0f0f0;
    outline-offset: 3px;
  }
`;

const Aside = styled.aside`
  min-height: 0;
  overflow: hidden;
`;

const DesktopLink = styled(Link)`
  position: absolute;
  top: 10px;
  right: 16px;
  z-index: 1;
  color: inherit;
  font-size: 13px;
`;

const More = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 2px solid #8a8a8a;
`;

const MoreRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 8px;
  font-size: 13px;
  line-height: 1.3;
`;

const MoreLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
`;
